// src/components/CompletePlanningApp.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Calendar, Clock, Users, User, LogOut, X, AlertCircle, CheckCircle, Plus } from 'lucide-react';
import { planningApiService } from '../services/planningApi';

// Utilisation du service API réel

// ===== COMPOSANTS UTILITAIRES =====

const LoadingSpinner = () => (
  <div className="flex items-center justify-center p-4">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
  </div>
);

// Fonction utilitaire pour inverser l'ordre nom/prénom
const formatWorkerName = (name) => {
  if (!name) return '';
  const parts = name.trim().split(/\s+/);
  if (parts.length <= 1) return name;
  // Inverser : prendre le dernier mot comme nom, le reste comme prénom
  const lastName = parts[parts.length - 1];
  const firstName = parts.slice(0, -1).join(' ');
  return `${lastName} ${firstName}`;
};

// Modal Tous les utilisateurs
const AllUsersModal = ({
  isOpen,
  onClose,
  users,
  setUsers,
  showToast,
  planningApiService
}) => {
  const [query, setQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all'); // all | admin | worker
  const [statusFilter, setStatusFilter] = useState('all'); // all | active | inactive

  if (!isOpen) return null;

  const filtered = users.filter(u => {
    const matchName = u.name?.toLowerCase().includes(query.toLowerCase());
    const matchRole = roleFilter === 'all' ? true : u.role === roleFilter;
    const matchStatus = statusFilter === 'all' ? true : (statusFilter === 'active' ? u.is_active : !u.is_active);
    return matchName && matchRole && matchStatus;
  });

  const toggleActive = async (user) => {
    try {
      const next = !user.is_active;
      await planningApiService.setUserActive(user.id, next);
      setUsers(prev => prev.map(x => x.id === user.id ? { ...x, is_active: next } : x));
      showToast(next ? 'Utilisateur activé' : 'Utilisateur désactivé');
    } catch (e) {
      showToast(e.response?.data?.error || "Erreur lors du changement d'état", 'error');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex items-end sm:items-center justify-center z-40" onClick={onClose}>
      <div className="bg-white w-full sm:w-[900px] max-h[90vh] sm:max-h-[90vh] rounded-t-2xl sm:rounded-2xl shadow-xl overflow-hidden flex flex-col"
           onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b flex items-center justify-between flex-shrink-0">
          <h3 className="text-lg font-semibold text-gray-800">Tous les utilisateurs</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4 overflow-y-auto flex-1">
          {/* Filtres */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <input
              type="text"
              placeholder="Rechercher par nom"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="border border-gray-300 rounded px-3 py-2 w-full"
            />
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="border border-gray-300 rounded px-3 py-2 w-full"
            >
              <option value="all">Tous les rôles</option>
              <option value="admin">Administrateurs</option>
              <option value="worker">Ouvriers</option>
            </select>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-gray-300 rounded px-3 py-2 w-full"
            >
              <option value="all">Tous les états</option>
              <option value="active">Actifs</option>
              <option value="inactive">Inactifs</option>
            </select>
          </div>

          {/* Liste */}
          <div className="border rounded-lg overflow-hidden max-h-96 overflow-y-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b sticky top-0">
                <tr>
                  <th className="text-left text-sm font-medium text-gray-700 px-3 py-2">Nom</th>
                  <th className="text-left text-sm font-medium text-gray-700 px-3 py-2">Rôle</th>
                  <th className="text-left text-sm font-medium text-gray-700 px-3 py-2">Statut</th>
                  <th className="text-right text-sm font-medium text-gray-700 px-3 py-2">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filtered.map(u => (
                  <tr key={u.id} className="hover:bg-gray-50">
                    <td className="px-3 py-2 text-sm text-gray-800">{formatWorkerName(u.name)}</td>
                    <td className="px-3 py-2 text-sm text-gray-600">{u.role}</td>
                    <td className="px-3 py-2 text-sm">
                      <span className={`px-2 py-1 rounded text-xs ${u.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'}`}>
                        {u.is_active ? 'Actif' : 'Inactif'}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right space-x-2">
                      <button
                        onClick={() => toggleActive(u)}
                        className={`text-sm ${u.is_active ? 'text-red-600 hover:text-red-800' : 'text-green-600 hover:text-green-800'}`}
                      >
                        {u.is_active ? 'Désactiver' : 'Activer'}
                      </button>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan="4" className="px-3 py-6 text-center text-sm text-gray-500">Aucun utilisateur</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

// Modal Admin avec état interne pour éviter les re-rendus
const AdminModal = ({ 
  isOpen, 
  onClose, 
  admins, 
  setAdmins, 
  showToast, 
  planningApiService 
}) => {
  // État interne de la modal pour éviter les re-rendus du parent
  const [isEditingAdmin, setIsEditingAdmin] = useState(false);
  const [adminForm, setAdminForm] = useState({ id: null, name: '', email: '', login: '', password: '' });

  // Gestionnaires d'événements stables locaux
  const handleNameChange = useCallback((e) => {
    setAdminForm(f => ({ ...f, name: e.target.value }));
  }, []);

  // champ email masqué (conservé pour compat API maj)

  const handleLoginChange = useCallback((e) => {
    setAdminForm(f => ({ ...f, login: e.target.value }));
  }, []);

  const handlePasswordChange = useCallback((e) => {
    setAdminForm(f => ({ ...f, password: e.target.value }));
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!adminForm.name || !adminForm.login) {
      showToast('Nom et login requis', 'error');
      return;
    }
    try {
      if (isEditingAdmin) {
        // Conserver l'email existant pour compat backend
        const updateData = { name: adminForm.name, email: adminForm.email };
        if (adminForm.password && adminForm.password.trim()) {
          updateData.password = adminForm.password;
        }
        const updatedAdmin = await planningApiService.updateAdmin(adminForm.id, updateData);
        setAdmins(prev => prev.map(a => a.id === adminForm.id ? updatedAdmin : a));
        showToast('Administrateur mis à jour');
      } else {
        const createData = { name: adminForm.name };
        if (adminForm.login && adminForm.login.trim()) {
          createData.login = adminForm.login.trim();
        }
        if (adminForm.password && adminForm.password.trim()) {
          createData.password = adminForm.password;
        }
        const newAdmin = await planningApiService.createAdmin(createData);
        setAdmins(prev => [...prev, newAdmin]);
        showToast('Administrateur ajouté');
      }
    } catch (error) {
      console.error('Erreur CRUD admin:', error);
      showToast(error.response?.data?.error || "Erreur lors de l'opération", 'error');
      return;
    }
    setAdminForm({ id: null, name: '', email: '', login: '', password: '' });
    setIsEditingAdmin(false);
  }, [adminForm, isEditingAdmin, planningApiService, setAdmins, showToast]);

  const handleCancel = useCallback(() => {
    setIsEditingAdmin(false);
    setAdminForm({ id: null, name: '', email: '', login: '', password: '' });
  }, []);

  const handleEdit = useCallback((admin) => {
    setIsEditingAdmin(true);
    setAdminForm({ 
      id: admin.id, 
      name: admin.name, 
      email: admin.email, 
      login: admin.email?.split('@')[0] || '', 
      password: '' 
    });
  }, []);

  const handleDelete = useCallback(async (admin) => {
    try {
      await planningApiService.deleteAdmin(admin.id);
      setAdmins(prev => prev.filter(x => x.id !== admin.id));
      showToast('Administrateur supprimé');
      if (isEditingAdmin && adminForm.id === admin.id) {
        setIsEditingAdmin(false);
        setAdminForm({ id: null, name: '', email: '', login: '', password: '' });
      }
    } catch (error) {
      console.error('Erreur suppression admin:', error);
      showToast(error.response?.data?.error || 'Erreur lors de la suppression', 'error');
    }
  }, [planningApiService, setAdmins, showToast, isEditingAdmin, adminForm.id]);

  const handleDowngrade = useCallback(async (admin) => {
    try {
      await planningApiService.changeUserRole(admin.id, 'worker');
      setAdmins(prev => prev.filter(x => x.id !== admin.id));
      showToast('Administrateur rétrogradé en ouvrier');
    } catch (error) {
      console.error('Erreur rétrogradation:', error);
      showToast(error.response?.data?.error || 'Erreur lors de la rétrogradation', 'error');
    }
  }, [planningApiService, setAdmins, showToast]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex items-end sm:items-center justify-center z-40" onClick={onClose}>
      <div className="bg-white w-full sm:w-[800px] max-h-[90vh] rounded-t-2xl sm:rounded-2xl shadow-xl overflow-hidden flex flex-col"
           onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b flex items-center justify-between flex-shrink-0">
          <h3 className="text-lg font-semibold text-gray-800">Gestion des administrateurs</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4 space-y-4 overflow-y-auto flex-1">
          {/* Formulaire */}
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
                <div>
                  <label htmlFor="admin-name" className="sr-only">Nom</label>
                  <input
                    key="admin-name-input"
                    id="admin-name"
                    name="name"
                    type="text"
                    placeholder="Nom"
                    value={adminForm.name}
                    onChange={handleNameChange}
                    autoComplete="name"
                    className="border border-gray-300 rounded px-3 py-2 w-full"
                  />
                </div>
                <div>
                  <label htmlFor="admin-login" className="sr-only">Login</label>
                  <input
                    key="admin-login-input"
                    id="admin-login"
                    name="login"
                    type="text"
                    placeholder="Login"
                    value={adminForm.login}
                    onChange={handleLoginChange}
                    autoComplete="username"
                    className="border border-gray-300 rounded px-3 py-2 w-full"
                  />
                </div>
                <div>
                  <label htmlFor="admin-password" className="sr-only">Mot de passe</label>
                  <input
                    key="admin-password-input"
                    id="admin-password"
                    name="password"
                    type="password"
                    placeholder="Mot de passe"
                    value={adminForm.password}
                    onChange={handlePasswordChange}
                    autoComplete="new-password"
                    className="border border-gray-300 rounded px-3 py-2 w-full"
                  />
                </div>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={handleSubmit}
                  className="bg-green-600 text-white px-3 py-2 rounded hover:bg-green-700 w-full sm:w-auto"
                >
                  {isEditingAdmin ? 'Enregistrer' : 'Ajouter'}
                </button>
                {isEditingAdmin && (
                  <button
                    onClick={handleCancel}
                    className="bg-gray-200 text-gray-800 px-3 py-2 rounded hover:bg-gray-300 w-full sm:w-auto"
                  >
                    Annuler
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Liste */}
          <div className="border rounded-lg overflow-hidden max-h-96 overflow-y-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b sticky top-0">
                <tr>
                  <th className="text-left text-sm font-medium text-gray-700 px-3 py-2">Nom</th>
                  <th className="text-left text-sm font-medium text-gray-700 px-3 py-2">Login</th>
                  <th className="text-right text-sm font-medium text-gray-700 px-3 py-2">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {admins.map(a => (
                  <tr key={a.id} className="hover:bg-gray-50">
                    <td className="px-3 py-2 text-sm text-gray-800">{formatWorkerName(a.name)}</td>
                    <td className="px-3 py-2 text-sm text-gray-600">{(a.email || '').split('@')[0] || ''}</td>
                    <td className="px-3 py-2 text-right space-x-2">
                      <button
                        onClick={() => handleEdit(a)}
                        className="text-blue-600 hover:text-blue-800 text-sm"
                      >
                        Modifier
                      </button>
                      <button
                        onClick={() => handleDowngrade(a)}
                        className="text-yellow-600 hover:text-yellow-800 text-sm"
                      >
                        Rétrograder ouvrier
                      </button>
                      <button
                        onClick={() => handleDelete(a)}
                        className="text-red-600 hover:text-red-800 text-sm"
                      >
                        Supprimer
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

const Toast = ({ message, type, onClose }) => (
  <div className={`fixed top-4 right-4 p-4 rounded-lg shadow-lg z-50 ${
    type === 'success' ? 'bg-green-500' : 'bg-red-500'
  } text-white`}>
    <div className="flex items-center">
      {type === 'success' ? 
        <CheckCircle className="w-5 h-5 mr-2" /> : 
        <AlertCircle className="w-5 h-5 mr-2" />
      }
      <span>{message}</span>
      <button onClick={onClose} className="ml-4 text-white hover:text-gray-200">
        <X className="w-4 h-4" />
      </button>
    </div>
  </div>
);

// ===== COMPOSANT PRINCIPAL =====

const CompletePlanningApp = () => {
  // États principaux
  const [currentUser, setCurrentUser] = useState(null);
  const [currentView, setCurrentView] = useState('login');
  const [currentWeek, setCurrentWeek] = useState(new Date());
  
  // Données
  const [workers, setWorkers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [planning, setPlanning] = useState({});
  
  // États de l'interface
  const [loading, setLoading] = useState(false);
  // Gestion des ouvriers (CRUD)
  const [isWorkerManagerOpen, setIsWorkerManagerOpen] = useState(false);
  const [isEditingWorker, setIsEditingWorker] = useState(false);
  const [workerForm, setWorkerForm] = useState({ id: null, name: '', email: '', login: '', password: '', worker_category: 'ouvrier' });
  // Gestion des administrateurs (CRUD)
  const [admins, setAdmins] = useState([]);
  const [isAdminManagerOpen, setIsAdminManagerOpen] = useState(false);
  const [toast, setToast] = useState(null);
  // Tous les utilisateurs (actifs/inactifs)
  const [allUsers, setAllUsers] = useState([]);
  const [isAllUsersOpen, setIsAllUsersOpen] = useState(false);
  // Filtre d'ouvriers pour le tableau de planning
  const [workerQuery, setWorkerQuery] = useState('');
  const [workerCategoryFilter, setWorkerCategoryFilter] = useState('all'); // all | ouvrier | apprenti | sous-traitant
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const searchInputRef = useRef(null);
  const planningContainerRef = useRef(null);
  
  // États pour la sélection par glisser-déposer
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionStart, setSelectionStart] = useState(null);
  const [selectedCells, setSelectedCells] = useState(new Set());
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [assignmentForm, setAssignmentForm] = useState({ projectName: '', hours: 8 });
  const isAnyModalOpen = isAdminManagerOpen || isWorkerManagerOpen || showAssignmentModal;

  // Fonctions utilitaires
  const getWeekDates = (date) => {
    const start = new Date(date);
    const day = start.getDay();
    const diff = start.getDate() - day + (day === 0 ? -6 : 1);
    start.setDate(diff);
    
    const dates = [];
    for (let i = 0; i < 5; i++) {
      const current = new Date(start);
      current.setDate(start.getDate() + i);
      dates.push(current);
    }
    return dates;
  };

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatDisplayDate = (date) => {
    return date.toLocaleDateString('fr-FR', { 
      weekday: 'short', 
      day: 'numeric', 
      month: 'short' 
    });
  };

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };


  // Fonctions de chargement des données
  const loadInitialData = useCallback(async () => {
    try {
      setLoading(true);

      // Vérifier d'abord si un utilisateur/token existe
      const savedUser = localStorage.getItem('user');
      const token = localStorage.getItem('authToken');

      if (!savedUser || !token) {
        // Pas connecté: afficher l'écran de login et ne pas appeler les endpoints protégés
        setCurrentUser(null);
        setCurrentView('login');
        return;
      }

      // Restaurer l'utilisateur et charger les données protégées
      try {
        const user = JSON.parse(savedUser);
        setCurrentUser(user);
        setCurrentView(user.role === 'admin' ? 'admin' : 'worker');
      } catch (e) {
        localStorage.removeItem('user');
        localStorage.removeItem('authToken');
        setCurrentUser(null);
        setCurrentView('login');
        return;
      }

      const [workersData, projectsData, adminsData] = await Promise.all([
        planningApiService.getWorkers(),
        planningApiService.getProjects(),
        planningApiService.getAdmins()
      ]);

      setWorkers(workersData);
      setProjects(projectsData);
      setAdmins(adminsData);
      // Précharger en arrière-plan la liste complète (non bloquant)
      planningApiService.getAllUsers().then(setAllUsers).catch(() => {});
    } catch (error) {
      // Nettoyer en cas d'auth invalide
      localStorage.removeItem('user');
      localStorage.removeItem('authToken');
      setCurrentUser(null);
      setCurrentView('login');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadPlanningData = useCallback(async () => {
    if (!currentUser) return; // Ne pas charger si pas connecté
    
    try {
      const weekDates = getWeekDates(currentWeek);
      const startDate = weekDates[0];
      const endDate = weekDates[4];
      
      const planningData = await planningApiService.getPlanning(startDate, endDate);
      setPlanning(planningData);
    } catch (error) {
      showToast('Erreur lors du chargement du planning', 'error');
      // Si erreur d'auth, déconnecter
      if (error.response?.status === 401) {
        localStorage.removeItem('user');
        localStorage.removeItem('authToken');
        setCurrentUser(null);
        setCurrentView('login');
      }
    }
  }, [currentWeek, currentUser]); // Dépendances explicites"

  // Charger les données initiales (une seule fois)
  useEffect(() => {
    // Nettoyer d'abord les anciens tokens mockés
    const token = localStorage.getItem('authToken');
    if (token && (token === 'fake-admin-token' || token === 'fake-worker-token')) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
    }
    
    loadInitialData();
  }, [loadInitialData]); // Dépendance explicite

  // Charger le planning quand la semaine change ou l'utilisateur
  useEffect(() => {
    if (currentUser) {
      loadPlanningData();
    }
  }, [loadPlanningData, currentUser]);
  
  // handleMouseUp défini ici pour que les hooks ci-dessous puissent l'utiliser
  

  // Effet pour gérer les événements de souris globaux
  useEffect(() => {
    const handleGlobalMouseUp = (e) => {
      if (isSearchFocused) return;
      if (isSelecting && !isAnyModalOpen) {
        // Vérifier si on clique sur un élément interactif
        const interactiveElements = ['BUTTON', 'INPUT', 'SELECT', 'A'];
        const isInteractiveElement = interactiveElements.includes(e.target.tagName) || 
                                   e.target.closest('button') || 
                                   e.target.closest('input') || 
                                   e.target.closest('select') || 
                                   e.target.closest('a');
        
        if (isInteractiveElement) {
          return;
        }
        
        // Nettoyer la sélection de texte du navigateur
        try {
          if (window.getSelection) {
            window.getSelection().removeAllRanges();
          }
        } catch (error) {
          // Ignore les erreurs de sélection
        }
        
        // Equivalent inline de handleMouseUp pour éviter un avertissement d'ordre de déclaration
        if (selectedCells.size > 0) {
          setShowAssignmentModal(true);
        }
        setIsSelecting(false);
      }
    };

    const handleGlobalKeyDown = (e) => {
      // Annuler la sélection avec Escape
      if (e.key === 'Escape' && (isSelecting || showAssignmentModal)) {
        e.preventDefault();
        setSelectedCells(new Set());
        setIsSelecting(false);
        setShowAssignmentModal(false);
        setAssignmentForm({ projectName: '', hours: 8 });
      }
    };

    if (isSelecting && !isAnyModalOpen) {
      document.addEventListener('mouseup', handleGlobalMouseUp);
      document.addEventListener('keydown', handleGlobalKeyDown);
      return () => {
        document.removeEventListener('mouseup', handleGlobalMouseUp);
        document.removeEventListener('keydown', handleGlobalKeyDown);
      };
    }

    if (showAssignmentModal) {
      document.addEventListener('keydown', handleGlobalKeyDown);
      return () => document.removeEventListener('keydown', handleGlobalKeyDown);
    }
  }, [isSelecting, selectedCells, isAnyModalOpen, isSearchFocused, showAssignmentModal]);

  // Fonctions d'authentification
  const handleLogin = async (login, password) => {
    try {
      setLoading(true);
      const result = await planningApiService.login(login, password);
      
      if (result.success) {
        setCurrentUser(result.user);
        setCurrentView(result.user.role === 'admin' ? 'admin' : 'worker');
        showToast(`Bienvenue, ${result.user.name}!`);

        // Charger immédiatement les données protégées après connexion
        try {
          const [workersData, projectsData, adminsData] = await Promise.all([
            planningApiService.getWorkers(),
            planningApiService.getProjects(),
            planningApiService.getAdmins()
          ]);
          setWorkers(workersData);
          setProjects(projectsData);
          setAdmins(adminsData);
          planningApiService.getAllUsers().then(setAllUsers).catch(() => {});
        } catch (e) {
          // En cas d'échec, rester connecté mais afficher un toast
          showToast('Erreur lors du chargement des données', 'error');
        }
      } else {
        showToast(result.error, 'error');
      }
    } catch (error) {
      showToast('Erreur de connexion', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    setCurrentUser(null);
    setCurrentView('login');
    showToast('Déconnexion réussie');
  };

  // Fonctions de gestion de la sélection par glisser-déposer
  const getCellId = (workerId, date) => `${workerId}-${date}`;
  // supprimer double définition
  const parseCellId = (cellId) => {
    const parts = cellId.split('-');
    const workerId = parseInt(parts[0]);
    const date = parts.slice(1).join('-'); // Rejoint les parties de date avec '-'
    return { workerId, date };
  };
  
  const handleMouseDown = (workerId, date, e) => {
    if (currentUser?.role !== 'admin' || isAnyModalOpen || isSearchFocused) return;
    
    // Empêcher tous les comportements par défaut
    e.preventDefault();
    e.stopPropagation();
    
    // Nettoyer les sélections existantes
    try {
      if (window.getSelection) {
        window.getSelection().removeAllRanges();
      }
    } catch (error) {
      // Ignore les erreurs
    }
    
    const cellId = getCellId(workerId, date);
    setIsSelecting(true);
    setSelectionStart({ workerId, date });
    setSelectedCells(new Set([cellId]));
  };
  
  const handleMouseEnter = (workerId, date) => {
    if (!isSelecting || currentUser?.role !== 'admin' || isAnyModalOpen || isSearchFocused) return;
    
    // Calculer toutes les cellules sélectionnées
    const selected = new Set();
    // Utiliser la liste affichée (filtrée/triée) pour éviter les décalages d'index
    const startWorkerIndex = filteredSortedWorkers.findIndex(w => w.id === selectionStart.workerId);
    const endWorkerIndex = filteredSortedWorkers.findIndex(w => w.id === workerId);
    const startDateIndex = weekDates.findIndex(d => formatDate(d) === selectionStart.date);
    const endDateIndex = weekDates.findIndex(d => formatDate(d) === date);
    
    // Garde-fous si l'un des index est introuvable
    if (startWorkerIndex === -1 || endWorkerIndex === -1 || startDateIndex === -1 || endDateIndex === -1) {
      setSelectedCells(new Set());
      return;
    }

    const minWorkerIndex = Math.min(startWorkerIndex, endWorkerIndex);
    const maxWorkerIndex = Math.max(startWorkerIndex, endWorkerIndex);
    const minDateIndex = Math.min(startDateIndex, endDateIndex);
    const maxDateIndex = Math.max(startDateIndex, endDateIndex);
    
    for (let wi = minWorkerIndex; wi <= maxWorkerIndex; wi++) {
      for (let di = minDateIndex; di <= maxDateIndex; di++) {
        if (filteredSortedWorkers[wi] && weekDates[di]) {
          selected.add(getCellId(filteredSortedWorkers[wi].id, formatDate(weekDates[di])));
        }
      }
    }
    
    setSelectedCells(selected);
  };
  
  
  
  const handleAssignmentSubmit = async (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    
    if (!selectedCells.size || !assignmentForm.projectName.trim()) return;
    
    try {
      setLoading(true);
      
      // Préparer les assignments pour la mise à jour en lot
      const assignments = [];
      selectedCells.forEach(cellId => {
        const { workerId, date } = parseCellId(cellId);
        assignments.push({
          workerId,
          date,
          projectName: assignmentForm.projectName.trim(),
          hours: assignmentForm.hours,
          color: getProjectColor(assignmentForm.projectName.trim())
        });
      });
      
      await planningApiService.bulkUpdatePlanning(assignments);
      
      // Mettre à jour le state local
      const newPlanning = { ...planning };
      assignments.forEach(({ workerId, date, projectName, hours, color }) => {
        if (!newPlanning[workerId]) {
          newPlanning[workerId] = {};
        }
        if (!newPlanning[workerId][date]) {
          newPlanning[workerId][date] = [];
        }
        
        const entryId = `${workerId}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        newPlanning[workerId][date].push({
          id: entryId,
          projectName,
          hours,
          color
        });
      });
      
      setPlanning(newPlanning);
      showToast(`Planning mis à jour pour ${selectedCells.size} cellule(s)`);
      
      // Reset
      setSelectedCells(new Set());
      setShowAssignmentModal(false);
      setAssignmentForm({ projectName: '', hours: 8 });
      setIsSelecting(false);
      
    } catch (error) {
      showToast('Erreur lors de la mise à jour', 'error');
    } finally {
      setLoading(false);
    }
  };
  
  const getProjectColor = (projectName) => {
    const project = projects.find(p => p.name === projectName);
    if (project) return project.color;
    
    // Couleurs par défaut pour les nouveaux projets
    const defaultColors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];
    const hash = projectName.split('').reduce((a, b) => {
      a = ((a << 5) - a) + b.charCodeAt(0);
      return a & a;
    }, 0);
    return defaultColors[Math.abs(hash) % defaultColors.length];
  };
  
  const handleDeleteEntry = async (workerId, date, entryId) => {
    try {
      setLoading(true);
      await planningApiService.deletePlanningEntry(entryId);
      
      setPlanning(prevPlanning => {
        const newPlanning = { ...prevPlanning };
        if (newPlanning[workerId] && newPlanning[workerId][date]) {
          newPlanning[workerId][date] = newPlanning[workerId][date].filter(entry => entry.id !== entryId);
          if (newPlanning[workerId][date].length === 0) {
            delete newPlanning[workerId][date];
          }
        }
        return newPlanning;
      });
      
      showToast('Entrée supprimée');
      
    } catch (error) {
      showToast('Erreur lors de la suppression', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour annuler la sélection
  const handleCancelSelection = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    
    setSelectedCells(new Set());
    setShowAssignmentModal(false);
    setAssignmentForm({ projectName: '', hours: 8 });
    setIsSelecting(false);
  };

  const weekDates = getWeekDates(currentWeek);

  // Workers filtrés et triés (mémoisés)
  const filteredSortedWorkers = useMemo(() => {
    const q = workerQuery.trim().toLowerCase();
    let base = q ? workers.filter(w => (w.name || '').toLowerCase().includes(q)) : workers;
    if (workerCategoryFilter !== 'all') {
      base = base.filter(w => (w.worker_category || 'ouvrier') === workerCategoryFilter);
    }
    return [...base].sort((a, b) => {
      const aParts = (a.name || '').trim().split(/\s+/);
      const bParts = (b.name || '').trim().split(/\s+/);
      const aLastName = aParts[aParts.length - 1] || '';
      const bLastName = bParts[bParts.length - 1] || '';
      const aFirstName = aParts.slice(0, -1).join(' ') || '';
      const bFirstName = bParts.slice(0, -1).join(' ') || '';
      if (aLastName !== bLastName) return aLastName.toLowerCase().localeCompare(bLastName.toLowerCase());
      return aFirstName.toLowerCase().localeCompare(bFirstName.toLowerCase());
    });
  }, [workers, workerQuery, workerCategoryFilter]);

  // Réfocus sans scroll du champ recherche uniquement si non déjà focus
  useEffect(() => {
    if (isSearchFocused) {
      const inputEl = searchInputRef.current;
      if (inputEl && document.activeElement !== inputEl) {
        try {
          inputEl.focus({ preventScroll: true });
        } catch {
          inputEl.focus();
        }
      }
    }
  }, [filteredSortedWorkers, isSearchFocused]);


  // ===== COMPOSANT DE CONNEXION =====
  const LoginView = () => {
    const [login, setLogin] = useState('');
    const [password, setPassword] = useState('');

    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-2xl p-8 w-full max-w-md">
          <div className="text-center mb-8">
            <div className="bg-blue-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <Calendar className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-gray-800">Planning Ouvriers</h1>
            <p className="text-gray-600 mt-2">Connectez-vous pour accéder à votre planning</p>
          </div>

          {loading && <LoadingSpinner />}

          {!loading && (
            <div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Identifiant
                </label>
                <input
                  type="text"
                  value={login}
                  onChange={(e) => setLogin(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="ex: admin ou adam.mohamed"
                />
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Mot de passe
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Votre mot de passe"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleLogin(login, password);
                    }
                  }}
                />
              </div>

              <button
                onClick={() => handleLogin(login, password)}
                disabled={!login || !password}
                className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Se connecter
              </button>
            </div>
          )}

        </div>
      </div>
    );
  };

  // ===== VUE ADMINISTRATEUR =====
  const AdminView = () => (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Calendar className="w-8 h-8 text-blue-600 mr-3" />
              <h1 className="text-xl font-semibold text-gray-800">Planning Ouvriers</h1>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-gray-600">Bienvenue, {currentUser.name}</span>
              <button
                onClick={handleLogout}
                className="flex items-center text-gray-600 hover:text-gray-800 transition-colors"
              >
                <LogOut className="w-4 h-4 mr-1" />
                Déconnexion
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Navigation de semaine */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => {
              const prevWeek = new Date(currentWeek);
              prevWeek.setDate(prevWeek.getDate() - 7);
              setCurrentWeek(prevWeek);
            }}
            className="flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            ← Semaine précédente
          </button>
          
          <h2 className="text-lg font-medium text-gray-800">
            Semaine du {weekDates[0].toLocaleDateString('fr-FR')} au {weekDates[4].toLocaleDateString('fr-FR')}
          </h2>
          
          <button
            onClick={() => {
              const nextWeek = new Date(currentWeek);
              nextWeek.setDate(nextWeek.getDate() + 7);
              setCurrentWeek(nextWeek);
            }}
            className="flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Semaine suivante →
          </button>
        </div>

        {/* Barre de recherche ouvriers */}
        <div className="mb-4 flex items-center gap-2">
          <input
            ref={searchInputRef}
            type="text"
            value={workerQuery}
            onChange={(e) => setWorkerQuery(e.target.value)}
            onFocus={() => setIsSearchFocused(true)}
            onBlur={() => setIsSearchFocused(false)}
            placeholder="Rechercher un ouvrier (nom ou prénom)"
            className="w-full sm:w-96 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            autoComplete="off"
          />
          <select
            value={workerCategoryFilter}
            onChange={(e) => setWorkerCategoryFilter(e.target.value)}
            onMouseDown={(e) => e.stopPropagation()}
            onMouseUp={(e) => e.stopPropagation()}
            className="p-2 border border-gray-300 rounded-lg"
          >
            <option value="all">Toutes catégories</option>
            <option value="ouvrier">Ouvrier</option>
            <option value="apprenti">Apprenti</option>
            <option value="sous-traitant">Sous-traitant</option>
            <option value="bureau">Bureau</option>
            <option value="intérim">Intérim</option>
          </select>
          {workerQuery && (
            <button
              onClick={() => setWorkerQuery('')}
              className="px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-200"
            >
              Effacer
            </button>
          )}
        </div>

        

        {/* Instructions pour la sélection */}
        {currentUser?.role === 'admin' && (
          <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-sm text-blue-800">
              <strong>Sélection multiple :</strong> Cliquez et glissez pour sélectionner plusieurs cellules, 
              puis attribuez un chantier à toutes les cellules sélectionnées.
            </p>
          </div>
        )}
        {isAdminManagerOpen && (
          <AdminModal 
            isOpen={isAdminManagerOpen}
            onClose={() => setIsAdminManagerOpen(false)}
            admins={admins}
            setAdmins={setAdmins}
            showToast={showToast}
            planningApiService={planningApiService}
          />
        )}
        {isAllUsersOpen && (
          <AllUsersModal
            isOpen={isAllUsersOpen}
            onClose={() => setIsAllUsersOpen(false)}
            users={allUsers}
            setUsers={setAllUsers}
            showToast={showToast}
            planningApiService={planningApiService}
          />
        )}

        {/* Tableau de planning */}
        <div 
          className="bg-white rounded-lg shadow-sm border overflow-hidden"
        >
          <div 
            ref={planningContainerRef}
            className="overflow-x-auto overflow-y-auto planning-container"
            onDragStart={(e) => e.preventDefault()}
            onMouseDown={(e) => {
              // Ignorer les éléments interactifs
              if (e.target.tagName === 'BUTTON' || e.target.closest('button') ||
                  e.target.tagName === 'INPUT' || e.target.closest('input') ||
                  e.target.tagName === 'SELECT' || e.target.closest('select')) {
                return;
              }
              
              // Empêcher le comportement par défaut qui peut causer le scroll
              e.preventDefault();
              e.stopPropagation();
              
              // Nettoyer la sélection native et retirer le focus du champ recherche
              try { 
                if (window.getSelection) {
                  window.getSelection().removeAllRanges(); 
                }
              } catch {}
              
              if (document.activeElement === searchInputRef.current) {
                try { 
                  searchInputRef.current?.blur(); 
                } catch {}
                setIsSearchFocused(false);
              }
            }}
            onMouseMove={(e) => {
              // Pendant la sélection, empêcher la sélection native et le drag
              if (isSelecting) {
                e.preventDefault();
              }
            }}
            onMouseUp={(e) => {
              try { window.getSelection && window.getSelection().removeAllRanges(); } catch {}
            }}
            style={{ userSelect: 'none', WebkitUserSelect: 'none', MozUserSelect: 'none', msUserSelect: 'none' }}
            tabIndex={-1}
          >
            <table className="w-full">
              <thead className="bg-gray-50 border-b sticky top-0 z-10">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700" style={{ minWidth: '200px' }}>
                    Ouvrier
                  </th>
                  {weekDates.map(date => (
                    <th key={formatDate(date)} className="px-4 py-3 text-center text-sm font-medium text-gray-700" style={{ minWidth: '120px' }}>
                      <div>{formatDisplayDate(date)}</div>
                      <div className="text-xs text-gray-500">{date.getDate()}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredSortedWorkers.map(worker => (
                  <tr key={worker.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-800 font-medium">
                      <div className="flex items-center gap-2">
                        <span>{formatWorkerName(worker.name)}</span>
                        {worker.worker_category && (
                          <span
                            className={
                              `inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ` +
                              (worker.worker_category === 'ouvrier' ? 'bg-blue-100 text-blue-800' :
                               worker.worker_category === 'apprenti' ? 'bg-green-100 text-green-800' :
                               worker.worker_category === 'sous-traitant' ? 'bg-purple-100 text-purple-800' :
                               worker.worker_category === 'bureau' ? 'bg-yellow-100 text-yellow-800' :
                               worker.worker_category === 'intérim' ? 'bg-red-100 text-red-800' :
                               'bg-gray-100 text-gray-800')
                            }
                            title={`Catégorie: ${worker.worker_category}`}
                          >
                            {worker.worker_category}
                          </span>
                        )}
                      </div>
                    </td>
                    {weekDates.map(date => {
                      const dateStr = formatDate(date);
                      const entries = planning[worker.id]?.[dateStr] || [];
                      const cellId = getCellId(worker.id, dateStr);
                      const isSelected = selectedCells.has(cellId);

                      return (
                        <td 
                          key={dateStr} 
                          className={`px-1 py-1 text-center relative ${
                            isSelected ? 'bg-blue-200 ring-2 ring-blue-400' : ''
                          }`}
                          onMouseDown={(e) => {
                            // Ne pas déclencher la sélection si on clique sur un bouton, input ou select
                            if (e.target.tagName === 'BUTTON' || e.target.closest('button') ||
                                e.target.tagName === 'INPUT' || e.target.closest('input') ||
                                e.target.tagName === 'SELECT' || e.target.closest('select')) {
                              return;
                            }
                            if (currentUser?.role === 'admin' && !isAnyModalOpen && !isSearchFocused) {
                              // Empêcher le comportement par défaut
                              e.preventDefault();
                              e.stopPropagation();
                              
                              // Nettoyer toute sélection de texte résiduelle du navigateur
                              try { 
                                if (window.getSelection) {
                                  window.getSelection().removeAllRanges(); 
                                }
                              } catch {}
                              handleMouseDown(worker.id, dateStr, e);
                            }
                          }}
                          onMouseEnter={() => handleMouseEnter(worker.id, dateStr)}
                          style={{ userSelect: 'none' }}
                        >
                          <div className="min-h-16 flex flex-col justify-start space-y-1 p-1">
                            {Array.isArray(entries) ? entries.map((entry, index) => (
                              <div 
                                key={entry.id || `entry-${index}`}
                                className="group relative"
                              >
                                <div
                                  className="px-2 py-1 text-xs text-white rounded font-medium cursor-pointer hover:opacity-80 transition-opacity"
                                  style={{ backgroundColor: entry.color || '#4B5563' }}
                                  title={`${entry.projectName || 'Projet'} - ${entry.hours || 8}h`}
                                >
                                  <div className="flex items-center justify-between">
                                    <span className="truncate max-w-16">{entry.projectName || 'Projet'}</span>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        handleDeleteEntry(worker.id, dateStr, entry.id);
                                      }}
                                      onMouseDown={(e) => e.stopPropagation()}
                                      className="opacity-0 group-hover:opacity-100 ml-1 text-white hover:text-red-200 transition-opacity"
                                      type="button"
                                    >
                                      <X className="w-3 h-3" />
                                    </button>
                                  </div>
                                  <div className="text-xs opacity-75">{entry.hours || 8}h</div>
                                </div>
                              </div>
                            )) : (
                              // Fallback pour les anciennes données (format objet au lieu de tableau)
                              entries.projectName && (
                                <div className="group relative">
                                  <div
                                    className="px-2 py-1 text-xs text-white rounded font-medium cursor-pointer hover:opacity-80 transition-opacity"
                                    style={{ backgroundColor: entries.color || getProjectColor(entries.projectName) }}
                                    title={`${entries.projectName} - ${entries.hours || 8}h`}
                                  >
                                    <div className="flex items-center justify-between">
                                      <span className="truncate max-w-16">{entries.projectName}</span>
                                      <button
                                        onClick={async (e) => {
                                          e.stopPropagation();
                                          e.preventDefault();
                                          // Pour l'ancien format, supprimer via API si on a un ID
                                          if (entries.id) {
                                            try {
                                              await planningApiService.deletePlanningEntry(entries.id);
                                              setPlanning(prev => {
                                                const newPlanning = { ...prev };
                                                if (newPlanning[worker.id]) {
                                                  delete newPlanning[worker.id][dateStr];
                                                }
                                                return newPlanning;
                                              });
                                              showToast('Entrée supprimée');
                                            } catch (error) {
                                              showToast('Erreur lors de la suppression', 'error');
                                            }
                                          } else {
                                            // Suppression locale pour les données sans ID
                                            setPlanning(prev => {
                                              const newPlanning = { ...prev };
                                              if (newPlanning[worker.id]) {
                                                delete newPlanning[worker.id][dateStr];
                                              }
                                              return newPlanning;
                                            });
                                            showToast('Entrée supprimée');
                                          }
                                        }}
                                        onMouseDown={(e) => e.stopPropagation()}
                                        className="opacity-0 group-hover:opacity-100 ml-1 text-white hover:text-red-200 transition-opacity"
                                        type="button"
                                      >
                                        <X className="w-3 h-3" />
                                      </button>
                                    </div>
                                    <div className="text-xs opacity-75">{entries.hours || 8}h</div>
                                  </div>
                                </div>
                              )
                            )}
                            
                            {(!Array.isArray(entries) ? !entries?.projectName : entries.length === 0) && (
                              <div className="h-12 flex items-center justify-center text-gray-300 hover:text-gray-400 cursor-pointer">
                                <Plus className="w-4 h-4" />
                              </div>
                            )}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Statistiques */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg shadow-sm border p-4">
            <div className="flex items-center">
              <Users className="w-8 h-8 text-blue-600 mr-3" />
              <div>
                <p className="text-sm text-gray-600">Total Ouvriers</p>
                <p className="text-2xl font-bold text-gray-800">{workers.length}</p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Modal d'affectation pour sélection multiple */}
        {showAssignmentModal && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" 
            onClick={handleCancelSelection}
          >
            <div 
              className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md" 
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-center mb-6">
                <h3 className="text-lg font-semibold text-gray-800">Affecter un projet</h3>
                <p className="text-sm text-gray-600 mt-1">
                  {selectedCells.size} cellule(s) sélectionnée(s)
                </p>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nom du projet
                  </label>
                  <input
                    type="text"
                    value={assignmentForm.projectName}
                    onChange={(e) => setAssignmentForm(prev => ({ ...prev, projectName: e.target.value }))}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Ex: CP, 1793, Formation..."
                    autoFocus
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nombre d'heures
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="24"
                    value={assignmentForm.hours}
                    onChange={(e) => setAssignmentForm(prev => ({ ...prev, hours: parseInt(e.target.value) || 8 }))}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                
                {/* Suggestions de projets existants */}
                {projects.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Projets existants
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {projects.map(project => (
                        <button
                          key={project.id}
                          onClick={() => setAssignmentForm(prev => ({ ...prev, projectName: project.name }))}
                          className="px-3 py-1 text-xs rounded-full text-white hover:opacity-80 transition-opacity"
                          style={{ backgroundColor: project.color }}
                        >
                          {project.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              <div className="flex space-x-3 mt-6">
                <button
                  onClick={handleCancelSelection}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={handleAssignmentSubmit}
                  disabled={!assignmentForm.projectName.trim() || loading}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Attribution...' : 'Attribuer'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // ===== VUE OUVRIER =====
  const WorkerView = () => {
    const workerPlanning = planning[currentUser.id] || {};
    
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <header className="bg-white shadow-sm border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <div className="flex items-center">
                <User className="w-8 h-8 text-blue-600 mr-3" />
                <h1 className="text-xl font-semibold text-gray-800">Mon Planning</h1>
              </div>
              <div className="flex items-center space-x-4">
                <span className="text-gray-600">Bonjour, {currentUser.name}</span>
                <button
                  onClick={handleLogout}
                  className="flex items-center text-gray-600 hover:text-gray-800 transition-colors"
                >
                  <LogOut className="w-4 h-4 mr-1" />
                  Déconnexion
                </button>
              </div>
            </div>
          </div>
        </header>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Navigation de semaine */}
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={() => {
                const prevWeek = new Date(currentWeek);
                prevWeek.setDate(prevWeek.getDate() - 7);
                setCurrentWeek(prevWeek);
              }}
              className="flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              ← Précédente
            </button>
            
            <h2 className="text-lg font-medium text-gray-800 text-center">
              Semaine du {weekDates[0].toLocaleDateString('fr-FR')} au {weekDates[4].toLocaleDateString('fr-FR')}
            </h2>
            
            <button
              onClick={() => {
                const nextWeek = new Date(currentWeek);
                nextWeek.setDate(nextWeek.getDate() + 7);
                setCurrentWeek(nextWeek);
              }}
              className="flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Suivante →
            </button>
          </div>

          {/* Planning de la semaine */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {weekDates.map(date => {
              const dateStr = formatDate(date);
              const dayData = workerPlanning[dateStr];
              const entries = Array.isArray(dayData) ? dayData : (dayData?.projectName ? [dayData] : []);

              return (
                <div key={dateStr} className="bg-white rounded-lg shadow-sm border p-4 hover:shadow-md transition-shadow">
                  <div className="text-center mb-3">
                    <p className="text-sm font-medium text-gray-700">
                      {formatDisplayDate(date)}
                    </p>
                    <p className="text-lg font-bold text-gray-800">{date.getDate()}</p>
                  </div>
                  
                  {entries.length > 0 ? (
                    <div className="space-y-2">
                      {entries.map((entry, index) => (
                        <div key={entry.id || index} className="text-center">
                          <div
                            className="px-3 py-2 text-white rounded-lg font-medium text-sm"
                            style={{ backgroundColor: entry.color || getProjectColor(entry.projectName) }}
                          >
                            {entry.projectName}
                          </div>
                          <p className="text-xs text-gray-600 mt-1">
                            <Clock className="w-3 h-3 inline mr-1" />
                            {entry.hours || 8}h
                          </p>
                        </div>
                      ))}
                      <div className="text-center pt-2 border-t border-gray-100">
                        <p className="text-sm font-medium text-gray-700">
                          Total: {entries.reduce((sum, entry) => sum + (entry.hours || 8), 0)}h
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center text-gray-400">
                      <div className="border-2 border-dashed border-gray-200 rounded-lg py-4">
                        <p className="text-sm">Repos</p>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Résumé de la semaine */}
          <div className="mt-8 bg-white rounded-lg shadow-sm border p-6">
            <h3 className="text-lg font-medium text-gray-800 mb-4">Résumé de la semaine</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <p className="text-2xl font-bold text-blue-600">
                  {weekDates.reduce((total, date) => {
                    const dateStr = formatDate(date);
                    const dayData = workerPlanning[dateStr];
                    const entries = Array.isArray(dayData) ? dayData : (dayData?.projectName ? [dayData] : []);
                    return total + entries.reduce((dayTotal, entry) => dayTotal + (entry.hours || 8), 0);
                  }, 0)}h
                </p>
                <p className="text-sm text-gray-600">Heures travaillées</p>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <p className="text-2xl font-bold text-green-600">
                  {weekDates.filter(date => {
                    const dateStr = formatDate(date);
                    const dayData = workerPlanning[dateStr];
                    return dayData && (Array.isArray(dayData) ? dayData.length > 0 : dayData.projectName);
                  }).length}
                </p>
                <p className="text-sm text-gray-600">Jours travaillés</p>
              </div>
              <div className="text-center p-4 bg-purple-50 rounded-lg">
                <p className="text-2xl font-bold text-purple-600">
                  {new Set(weekDates.flatMap(date => {
                    const dateStr = formatDate(date);
                    const dayData = workerPlanning[dateStr];
                    const entries = Array.isArray(dayData) ? dayData : (dayData?.projectName ? [dayData] : []);
                    return entries.map(entry => entry.projectName);
                  })).size}
                </p>
                <p className="text-sm text-gray-600">Projets différents</p>
              </div>
            </div>
          </div>

          {/* Historique récent (bonus) */}
          <div className="mt-8 bg-white rounded-lg shadow-sm border p-6">
            <h3 className="text-lg font-medium text-gray-800 mb-4">Activité récente</h3>
            <div className="space-y-3">
              {Array.from(new Set(weekDates.flatMap(date => {
                const dateStr = formatDate(date);
                const dayData = workerPlanning[dateStr];
                const entries = Array.isArray(dayData) ? dayData : (dayData?.projectName ? [dayData] : []);
                return entries.map(entry => entry.projectName);
              }))).slice(0, 3).map((projectName, index) => {
                const projectHours = weekDates.reduce((total, date) => {
                  const dateStr = formatDate(date);
                  const dayData = workerPlanning[dateStr];
                  const entries = Array.isArray(dayData) ? dayData : (dayData?.projectName ? [dayData] : []);
                  return total + entries
                    .filter(entry => entry.projectName === projectName)
                    .reduce((sum, entry) => sum + (entry.hours || 8), 0);
                }, 0);
                
                const projectDays = weekDates.filter(date => {
                  const dateStr = formatDate(date);
                  const dayData = workerPlanning[dateStr];
                  const entries = Array.isArray(dayData) ? dayData : (dayData?.projectName ? [dayData] : []);
                  return entries.some(entry => entry.projectName === projectName);
                }).length;
                
                const project = projects.find(p => p.name === projectName);
                const color = project?.color || getProjectColor(projectName);
                
                return (
                  <div key={projectName} className="flex items-center p-3 bg-gray-50 rounded-lg">
                    <div 
                      className="w-3 h-3 rounded-full mr-3"
                      style={{ backgroundColor: color }}
                    ></div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-800">Projet {projectName}</p>
                      <p className="text-xs text-gray-600">Travaillé {projectDays} jour(s) cette semaine</p>
                    </div>
                    <span className="text-xs text-gray-500">
                      {projectHours}h
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Rendu conditionnel
  if (currentView === 'login') {
    return (
      <>
        <LoginView />
        {toast && (
          <Toast 
            message={toast.message} 
            type={toast.type} 
            onClose={() => setToast(null)} 
          />
        )}
      </>
    );
  } else if (currentView === 'admin') {
    return (
      <>
        <AdminView />
        {/* Gestion des admins et ouvriers */}
        <div className="fixed bottom-4 right-4 flex flex-col space-y-2">
          <button
            onClick={() => {
              setIsAdminManagerOpen(v => !v);
            }}
            className="bg-purple-600 text-white px-4 py-2 rounded-lg shadow hover:bg-purple-700"
          >
            {isAdminManagerOpen ? 'Fermer la gestion des admins' : 'Gérer les admins'}
          </button>
          <button
            onClick={() => {
              setIsWorkerManagerOpen(v => !v);
              setIsEditingWorker(false);
              setWorkerForm({ id: null, name: '', email: '', login: '', password: '', worker_category: 'ouvrier' });
            }}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg shadow hover:bg-blue-700"
          >
            {isWorkerManagerOpen ? 'Fermer la gestion des ouvriers' : 'Gérer les ouvriers'}
          </button>
          <button
            onClick={async () => {
              try {
                const users = await planningApiService.getAllUsers();
                setAllUsers(users);
                setIsAllUsersOpen(true);
              } catch (e) {
                showToast("Erreur lors du chargement des utilisateurs", 'error');
              }
            }}
            className="bg-gray-700 text-white px-4 py-2 rounded-lg shadow hover:bg-gray-800"
          >
            Tous les utilisateurs
          </button>
        </div>

        {isWorkerManagerOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-30 flex items-end sm:items-center justify-center z-40" onClick={() => setIsWorkerManagerOpen(false)}>
            <div className="bg-white w-full sm:w-[800px] max-h-[90vh] rounded-t-2xl sm:rounded-2xl shadow-xl overflow-hidden flex flex-col"
                 onClick={(e) => e.stopPropagation()}>
              <div className="p-4 border-b flex items-center justify-between flex-shrink-0">
                <h3 className="text-lg font-semibold text-gray-800">Gestion des ouvriers</h3>
                <button onClick={() => setIsWorkerManagerOpen(false)} className="text-gray-500 hover:text-gray-700">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-4 space-y-4 overflow-y-auto flex-1">
                {/* Formulaire */}
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="space-y-3">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
                      <div>
                        <label htmlFor="worker-name" className="sr-only">Nom</label>
                        <input
                          key="worker-name-input"
                          id="worker-name"
                          name="name"
                          type="text"
                          placeholder="Nom"
                          value={workerForm.name}
                          onChange={(e) => setWorkerForm(f => ({ ...f, name: e.target.value }))}
                          autoComplete="name"
                          className="border border-gray-300 rounded px-3 py-2 w-full"
                        />
                      </div>
                      <div>
                        <label htmlFor="worker-login" className="sr-only">Login</label>
                        <input
                          key="worker-login-input"
                          id="worker-login"
                          name="login"
                          type="text"
                          placeholder="Login"
                          value={workerForm.login}
                          onChange={(e) => setWorkerForm(f => ({ ...f, login: e.target.value }))}
                          autoComplete="username"
                          className="border border-gray-300 rounded px-3 py-2 w-full"
                        />
                      </div>
                      <div>
                        <label htmlFor="worker-password" className="sr-only">Mot de passe</label>
                        <input
                          key="worker-password-input"
                          id="worker-password"
                          name="password"
                          type="password"
                          placeholder="Mot de passe"
                          value={workerForm.password}
                          onChange={(e) => setWorkerForm(f => ({ ...f, password: e.target.value }))}
                          autoComplete="new-password"
                          className="border border-gray-300 rounded px-3 py-2 w-full"
                        />
                      </div>
                      <div>
                        <label htmlFor="worker-category" className="sr-only">Catégorie</label>
                        <select
                          key="worker-category-input"
                          id="worker-category"
                          name="worker_category"
                          value={workerForm.worker_category}
                          onChange={(e) => setWorkerForm(f => ({ ...f, worker_category: e.target.value }))}
                          className="border border-gray-300 rounded px-3 py-2 w-full"
                        >
                          <option value="ouvrier">Ouvrier</option>
                          <option value="apprenti">Apprenti</option>
                          <option value="sous-traitant">Sous-traitant</option>
                          <option value="bureau">Bureau</option>
                          <option value="intérim">Intérim</option>
                        </select>
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={async () => {
                          if (!workerForm.name || !workerForm.login) {
                            showToast('Nom et login requis', 'error');
                            return;
                          }
                          try {
                            if (isEditingWorker) {
                              // Mise à jour
                              const updateData = {
                                name: workerForm.name,
                                email: workerForm.email,
                                worker_category: workerForm.worker_category
                              };
                              if (workerForm.password && workerForm.password.trim()) {
                                updateData.password = workerForm.password;
                              }
                              const updatedWorker = await planningApiService.updateWorker(workerForm.id, updateData);
                              setWorkers(prev => prev.map(w => w.id === workerForm.id ? updatedWorker : w));
                              showToast('Ouvrier mis à jour');
                            } else {
                              // Création
                              const createData = {
                                name: workerForm.name,
                                worker_category: workerForm.worker_category
                              };
                              if (workerForm.login && workerForm.login.trim()) {
                                createData.login = workerForm.login.trim();
                              }
                              if (workerForm.password && workerForm.password.trim()) {
                                createData.password = workerForm.password;
                              }
                              const newWorker = await planningApiService.createWorker(createData);
                              setWorkers(prev => [...prev, newWorker]);
                              showToast('Ouvrier ajouté');
                            }
                          } catch (error) {
                            console.error('Erreur CRUD ouvrier:', error);
                            showToast(error.response?.data?.error || 'Erreur lors de l\'opération', 'error');
                            return;
                          }
                          setWorkerForm({ id: null, name: '', email: '', login: '', password: '', worker_category: 'ouvrier' });
                          setIsEditingWorker(false);
                        }}
                        className="bg-green-600 text-white px-3 py-2 rounded hover:bg-green-700 w-full sm:w-auto"
                      >
                        {isEditingWorker ? 'Enregistrer' : 'Ajouter'}
                      </button>
                      {isEditingWorker && (
                        <button
                          onClick={() => { setIsEditingWorker(false); setWorkerForm({ id: null, name: '', email: '' }); }}
                          className="bg-gray-200 text-gray-800 px-3 py-2 rounded hover:bg-gray-300 w-full sm:w-auto"
                        >
                          Annuler
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Liste */}
                <div className="border rounded-lg overflow-hidden max-h-96 overflow-y-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b sticky top-0">
                      <tr>
                        <th className="text-left text-sm font-medium text-gray-700 px-3 py-2">Nom</th>
                        <th className="text-left text-sm font-medium text-gray-700 px-3 py-2">Login</th>
                        <th className="text-right text-sm font-medium text-gray-700 px-3 py-2">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {[...workers].sort((a, b) => {
                        // Trier par nom de famille (dernier mot) puis par prénom
                        const aParts = (a.name || '').trim().split(/\s+/);
                        const bParts = (b.name || '').trim().split(/\s+/);
                        const aLastName = aParts[aParts.length - 1] || '';
                        const bLastName = bParts[bParts.length - 1] || '';
                        const aFirstName = aParts.slice(0, -1).join(' ') || '';
                        const bFirstName = bParts.slice(0, -1).join(' ') || '';
                        
                        if (aLastName !== bLastName) return aLastName.toLowerCase().localeCompare(bLastName.toLowerCase());
                        return aFirstName.toLowerCase().localeCompare(bFirstName.toLowerCase());
                      }).map(w => (
                        <tr key={w.id} className="hover:bg-gray-50">
                          <td className="px-3 py-2 text-sm text-gray-800">{formatWorkerName(w.name)}</td>
                          <td className="px-3 py-2 text-sm text-gray-600">{(w.email || '').split('@')[0] || ''}</td>
                          <td className="px-3 py-2 text-right space-x-2">
                            <button
                              onClick={() => { 
                                setIsEditingWorker(true); 
                                setWorkerForm({ 
                                  id: w.id, 
                                  name: w.name, 
                                  email: w.email, 
                                  login: w.email?.split('@')[0] || '', 
                                  password: '',
                                  worker_category: w.worker_category || 'ouvrier'
                                }); 
                              }}
                              className="text-blue-600 hover:text-blue-800 text-sm"
                            >
                              Modifier
                            </button>
                            <button
                              onClick={async () => {
                                try {
                                  await planningApiService.changeUserRole(w.id, 'admin');
                                  const promoted = { ...w, role: 'admin' };
                                  setWorkers(prev => prev.filter(x => x.id !== w.id));
                                  setAdmins(prev => [...prev, promoted]);
                                  showToast('Utilisateur promu administrateur');
                                } catch (error) {
                                  console.error('Erreur promotion admin:', error);
                                  showToast(error.response?.data?.error || 'Erreur lors de la promotion', 'error');
                                }
                              }}
                              className="text-purple-600 hover:text-purple-800 text-sm"
                            >
                              Promouvoir admin
                            </button>
                            <button
                              onClick={async () => {
                                try {
                                  // Suppression via API
                                  await planningApiService.deleteWorker(w.id);
                                  setWorkers(prev => prev.filter(x => x.id !== w.id));
                                  // Nettoyer le planning du worker supprimé
                                  setPlanning(prev => {
                                    const updated = { ...prev };
                                    delete updated[w.id];
                                    return updated;
                                  });
                                  showToast('Ouvrier supprimé');
                                  if (isEditingWorker && workerForm.id === w.id) {
                                    setIsEditingWorker(false);
                                    setWorkerForm({ id: null, name: '', email: '', login: '', password: '' });
                                  }
                                } catch (error) {
                                  console.error('Erreur suppression ouvrier:', error);
                                  showToast(error.response?.data?.error || 'Erreur lors de la suppression', 'error');
                                }
                              }}
                              className="text-red-600 hover:text-red-800 text-sm"
                            >
                              Supprimer
                            </button>
                          </td>
                        </tr>) )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}
        {toast && (
          <Toast 
            message={toast.message} 
            type={toast.type} 
            onClose={() => setToast(null)} 
          />
        )}
      </>
    );
  } else if (currentView === 'worker') {
    return (
      <>
        <WorkerView />
        {toast && (
          <Toast 
            message={toast.message} 
            type={toast.type} 
            onClose={() => setToast(null)} 
          />
        )}
      </>
    );
  }

  return null;
};

export default CompletePlanningApp;